import {
  Button,
  Colors,
  IcArrowRight,
  IcCheckInFilled,
  IcPOS,
  Modal,
  Text,
} from '@fozito/common-ui';
import authApis from 'modules/auth/services/apis';
import { SHOP_DEVICE_TYPE } from 'modules/POS/Cashier/cashier.constants';
import { forwardRef, useImperativeHandle, useRef, useState, useTransition } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import navigate from 'utils/navigate';
import storage, { getDeviceId } from 'utils/sessionStorage';
import { setPageLoading } from 'widgets/PageLoading';

type IStationItem = {
  id: number;
  stationNumber: number;
};

type Props = {};
type Ref = {
  open: (storeId: string, totalStations: number, device_type: SHOP_DEVICE_TYPE) => void;
};
export const useModalChooseDeviceRef = () => useRef<Ref>(null);
const ModalChooseDevice = forwardRef<Ref, Props>(({ }, ref) => {
  const [stations, setStations] = useState<
    {
      id: number;
      stationNumber: number;
    }[]
  >([]);
  const [storeId, setStoreId] = useState<string>('');
  const [deviceType, setDeviceType] = useState<SHOP_DEVICE_TYPE>();
  const [, startTransition] = useTransition();
  const onClose = () => setStoreId('');
  const onOpen: Ref['open'] = (_storeId, totalStations, _deviceType) => startTransition(() => {
    setStations(
      Array.from(Array(totalStations).keys()).map((o) => ({
        id: o + 1,
        stationNumber: o + 1,
      }))
    );
    setStoreId(_storeId);
    setDeviceType(_deviceType);
  });

  const onChooseStation = async (o: IStationItem) => {
    setPageLoading(true);
    const deviceId = getDeviceId();
    try {
      const res: IResponseData<any> = await authApis.addPOSDevice(
        deviceId,
        o.stationNumber,
        deviceType || '',
      );

      if (res.data.data) {
        storage.station_number.set(o.stationNumber?.toString());
        storage.device_id.set(deviceId);
        storage.shop_id.set(storeId);
        storage.device_type.set(deviceType || '');
        onClose();

        if (deviceType !== SHOP_DEVICE_TYPE.POS)
          navigate(`/store/${storeId}/pos-cs`);
        else {
          navigate(`/store/${storeId}`);
        }
      }
    } catch (error) { } finally {
      setPageLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
  }));

  return (
    <StyledModal title={deviceType !== SHOP_DEVICE_TYPE.POS ? 'CHECK-IN STATIONS' : 'POS STATIONS'} isOpen={!!storeId} onClose={onClose} width={900}>
      <StyledDevices>
        <div className='content'>
          {stations.map((o) => (
            <button
              key={o.id}
              className='button-choose-device'
              onClick={() => onChooseStation(o)}
            >
              <div className='device-item'>
                <div className='device-info'>
                  {deviceType !== SHOP_DEVICE_TYPE.POS ?
                    <IcCheckInFilled width={80} height={80} fill={Colors.neutral.bg[6]} />
                    :
                    <IcPOS width={80} height={80} fill={Colors.neutral.bg[6]} />
                  }
                  <POS className='name-device'>STATION {o.stationNumber}</POS>
                </div>
                <IcArrowRight
                  width={48}
                  height={48}
                  fill={Colors.neutral.bg[7]}
                />
              </div>
            </button>
          ))}
        </div>
      </StyledDevices>
      <StyledFooter>
        <Button
          type='button'
          type_cancel
          className='btn-close'
          onClick={onClose}
        >
          Close
        </Button>
      </StyledFooter>
    </StyledModal>
  );
});
ModalChooseDevice.displayName = 'ModalChooseDevice';
export default ModalChooseDevice;

const StyledModal = styled(Modal)`
  width: 900px;
  background: #f5f5f5;
  max-width: 900px;
`;

const StyledDevices = styled.div`
  padding-top: 16px;
  .content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 0px 24px 40px 24px;

    & > :nth-child(odd):last-child {
      grid-column: span 2;
    }
  }
  button {
    &:hover {
      background: #eee;
      opacity: 0.8;
    }
  }
  .row-device {
    padding: 0 24px 40px;
  }

  .button-choose-device {
    width: 100%;
    padding: 0;
    box-shadow: 0px 4px 8px -3px #2d23424d, 0px 2px 4px 0px #2c234266;
  }

  .device-item {
    width: 100%;
    height: 164px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 48px;
    border-radius: 4px;
    border: 1px solid #ccd4dc;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4),
      0px 4px 8px -3px rgba(45, 35, 66, 0.3), 0px -3px 0px 0px #d6d6e7 inset;
  }

  .device-info {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .name-device {
    font-size: 30px;
    font-family: 'Poppins';
    font-weight: 400;
    color: #1d2129;
  }
`;

const StyledFooter = styled.div`
  width: 100%;
  padding: 16px;
  background: #ffffff;
  box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.15);

  .btn-close {
    width: 100%;
  }
`;

const POS = styled(Text)`
  font-family: Poppins;
  font-weight: 400;
  font-size: 32px;
  line-height: 48px;
  letter-spacing: 0%;
  color: ${Colors.neutral.text[5]};
`;
